import { useCallback, useState } from 'react';
import {
  GrcButton,
  GrcButtonType,
  GrcCalendar,
  GrcDatePicker,
  GrcNotify,
  GrcPopover,
  GrcPopoverPosition,
  NotifyType
} from '@grc/ui-package';
import { tabEvents } from '@grc/ui-utility';
import { useI18nState } from '../../stores/settings/language-context';
import I18N from '../../i18n/interface';
import {
  DateFilterType,
  IDateRangeFilterProps
} from './date-range-filter.interface';
import dayjs from 'dayjs';

import './date-range-filter.scss';

const viewsFilters: {
  id: DateFilterType;
  code: string;
  labelKey: keyof I18N;
}[] = [
  { id: 1, code: 'today', labelKey: 'Today' },
  { id: 2, code: 'day', labelKey: 'Day' },
  { id: 3, code: 'dateRange', labelKey: 'DateRange' }
];

export default function DateRangeFilter({
  value,
  onChange,
  idPrefix
}: IDateRangeFilterProps) {
  const { fromDate, dateFilterType } = value;
  const { localeData } = useI18nState();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedFilter, setSelectedFilter] =
    useState<DateFilterType>(dateFilterType);
  const [showCalenderPopover, setShowCalenderPopover] = useState(false);
  const [showDateRangePopover, setShowDateRangePopover] = useState(false);
  const [appliedFilter, setAppliedFilter] = useState<{
    type: DateFilterType;
    startDate: Date | null;
    endDate: Date | null;
  }>({ type: dateFilterType, startDate: fromDate, endDate: fromDate });

  const dayFilterId = `${idPrefix}_filter_day`;
  const dateRangeFilterId = `${idPrefix}_filter_dateRange`;

  const handleFilterClick = useCallback(
    (id: DateFilterType) => {
      setSelectedFilter(id);
      if (id === 2) {
        setShowCalenderPopover(true);
        setShowDateRangePopover(false);
      } else if (id === 3) {
        setShowDateRangePopover(true);
        setShowCalenderPopover(false);
        if (appliedFilter.type === 2) {
          setStartDate(appliedFilter.startDate);
          setEndDate(appliedFilter.endDate);
        }
      } else {
        setShowDateRangePopover(false);
        setShowCalenderPopover(false);
        const today = new Date();
        onChange({ fromDate: today, toDate: today, dateFilterType: 1 });
        setStartDate(null);
        setEndDate(null);
        setAppliedFilter({ type: 1, startDate: today, endDate: today });
      }
    },
    [onChange, appliedFilter]
  );

  const handleSearch = useCallback(() => {
    if (startDate && endDate && startDate.valueOf() <= endDate.valueOf()) {
      onChange({ fromDate: startDate, toDate: endDate, dateFilterType: 3 });
      setAppliedFilter({ type: 3, startDate, endDate });
      setShowDateRangePopover(false);
    } else {
      GrcNotify({
        message: localeData.DateValidationMessage,
        status: NotifyType.Warning
      });
    }
  }, [startDate, endDate, onChange, localeData]);

  const handleDateRangePopoverClose = useCallback(() => {
    setShowDateRangePopover(false);
    setStartDate(appliedFilter.startDate);
    setEndDate(appliedFilter.endDate);
    setSelectedFilter(appliedFilter.type);
  }, [appliedFilter]);

  const handleCalenderPopoverClose = useCallback(() => {
    setShowCalenderPopover(false);
    setSelectedFilter(appliedFilter.type);
    setStartDate(null);
    setEndDate(null);
  }, [appliedFilter]);

  return (
    <div
      className="d-flex align-items-center"
      data-testid={`${idPrefix}_date_filter`}
    >
      <div className="views-day-button-group-container">
        {viewsFilters.map(item => (
          <label
            data-testid={`${idPrefix}_filter_${item.code}`}
            key={item.id}
            id={`${idPrefix}_filter_${item.code}`}
            className={`views-styling-mode ${selectedFilter === item.id ? 'active' : ''}`}
            onClick={() => handleFilterClick(item.id)}
            {...tabEvents()}
          >
            {localeData[item.labelKey] as string}
          </label>
        ))}
      </div>

      {showCalenderPopover && (
        <div className="calender-popover">
          <GrcPopover
            showEvent="click"
            id={`${idPrefix}_calendar_popover`}
            target={`#${dayFilterId}`}
            width="100%"
            maxWidth={330}
            height="auto"
            showCloseButton
            showTitle
            onHiding={handleCalenderPopoverClose}
            position={GrcPopoverPosition.Bottom}
            hideOnOutsideClick
          >
            <div
              data-testid={`${idPrefix}_calendar_container`}
              className="p-2 calendar-container d-flex align-items-center justify-content-center"
            >
              <GrcCalendar
                id={`${idPrefix}_calendar`}
                value={fromDate}
                onValueChange={val => {
                  const picked = Array.isArray(val) ? val[0] : val;
                  const date = new Date(picked);
                  onChange({ fromDate: date, toDate: date, dateFilterType: 2 });
                  setAppliedFilter({ type: 2, startDate: date, endDate: date });
                  setShowCalenderPopover(false);
                }}
                onOptionChanged={() => {
                  setStartDate(null);
                  setEndDate(null);
                }}
              />
            </div>
          </GrcPopover>
        </div>
      )}

      {showDateRangePopover && (
        <GrcPopover
          showEvent="click"
          id={`${idPrefix}_daterange_popover`}
          target={`#${dateRangeFilterId}`}
          width={250}
          height={270}
          showCloseButton
          showTitle
          onHiding={handleDateRangePopoverClose}
          position={GrcPopoverPosition.Bottom}
          hideOnOutsideClick
        >
          <div className="p-2">
            <label className="grc-label">{localeData.From}</label>
            <GrcDatePicker
              id={`${idPrefix}_from_date`}
              value={startDate ? dayjs(startDate).startOf('day') : null}
              onChange={val =>
                setStartDate(val ? val.startOf('day').toDate() : null)
              }
            />
          </div>
          <div className="p-2">
            <label className="grc-label">{localeData.To}</label>
            <GrcDatePicker
              id={`${idPrefix}_to_date`}
              value={endDate ? dayjs(endDate).startOf('day') : null}
              onChange={val =>
                setEndDate(val ? val.startOf('day').toDate() : null)
              }
            />
          </div>
          <div className="p-2 mt-2 d-flex align-items-center justify-content-around">
            <GrcButton
              id={`${idPrefix}_filter_clear_btn`}
              className="grc-btn-secondary w-50"
              type={GrcButtonType.Normal}
              text={localeData.Clear}
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
              }}
            />
            <GrcButton
              id={`${idPrefix}_filter_apply_btn`}
              type={GrcButtonType.Default}
              text={localeData.Apply}
              className="grc-btn-primary w-50"
              disabled={!startDate || !endDate}
              onClick={handleSearch}
            />
          </div>
        </GrcPopover>
      )}
    </div>
  );
}
